import { useTranslation } from "react-i18next";
import BoardCard from "./BoardCard";

// ---------------------------------------------------------------------------
// The unplaced tray.
//
// There is no separate "unplaced" collection on the server: a placement with
// weekday=null / period=null IS the tray. So this panel is just the board's
// other half, and it is the first thing an over-subscribed school looks at —
// every card here carries the solver's own `unplaced_reason`.
//
// It doubles as a drop target: with a placed card selected, the panel turns
// into a dashed indigo zone and clicking it (or the explicit button, which is
// the keyboard path) PATCHes the card back to weekday:null / period:null.
// ---------------------------------------------------------------------------

export default function UnplacedTray({
  cards,
  view,
  selectedUuid,
  busyUuid,
  canReceive = false,
  readOnly = false,
  onCardClick,
  onSendToTray,
  onToggleLock,
}) {
  const { t } = useTranslation();

  return (
    <aside
      onClick={(event) => {
        // Empty panel space acts as the drop zone; real controls keep their own
        // behaviour. The explicit button below is the accessible equivalent.
        if (canReceive && !event.target.closest("button")) onSendToTray();
      }}
      className={`rounded-2xl border bg-white p-4 shadow-sm transition-colors ${
        canReceive
          ? "border-2 border-dashed border-indigo-400 bg-indigo-50/40 cursor-pointer"
          : "border-slate-200"
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          {t("timetable.trayTitle")}
        </h3>
        <span
          className={`text-[11px] font-black px-2 py-0.5 rounded-md ${
            cards.length > 0 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
          }`}
        >
          {cards.length}
        </span>
      </div>
      <p className="mt-1 text-[11px] font-medium text-slate-500 leading-relaxed">
        {t("timetable.trayHint")}
      </p>

      {canReceive && (
        <button
          type="button"
          onClick={onSendToTray}
          className="mt-3 w-full px-3 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 shadow-lg shadow-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
        >
          {t("timetable.sendToTray")}
        </button>
      )}

      {cards.length === 0 ? (
        <div className="mt-4 flex flex-col items-center justify-center text-center py-10 px-4 rounded-xl border-2 border-dashed border-slate-200 bg-white/50">
          <span className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mb-3">
            <svg
              className="w-6 h-6 text-emerald-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </span>
          <p className="text-xs font-bold text-slate-700">{t("timetable.trayEmpty")}</p>
          <p className="mt-1 text-[11px] text-slate-500">{t("timetable.trayEmptyHint")}</p>
        </div>
      ) : (
        <ul className="mt-3 space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {cards.map((card) => (
            <li key={card.uuid}>
              <BoardCard
                card={card}
                view={view}
                compact
                selected={card.uuid === selectedUuid}
                busy={card.uuid === busyUuid}
                readOnly={readOnly}
                onSelect={onCardClick}
                onToggleLock={onToggleLock}
              />
              {card.unplaced_reason && (
                <p className="mt-1 px-1 text-[10px] font-semibold text-amber-700 leading-snug">
                  {card.unplaced_reason}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
